/**
 * Consumable Class
 *
 * Extends Item with a timed use that restores something. Unlike a weapon, a
 * consumable has no state of its own on a player — the stack count in the
 * inventory slot is the whole of it, and the in-progress use lives in
 * ConsumableSystem for as long as it lasts.
 *
 * Consumables are the first kind added after 5D generalised the content
 * pipeline: this file, its registry, `ConsumableSystem`, and a line in
 * `ItemKinds.js` are the whole of it. Pickups, loot rolls, inventory
 * serialization and the collect path all work already.
 */

import { Item, ItemType } from './Item.js';

/** What a consumable's use does when it completes. */
const ConsumableEffectType = {
  SHIELD_RESTORE: 'shield_restore',
  HEALTH_RESTORE: 'health_restore'
};

const DEFAULT_USE_TIME = 1000;

class Consumable extends Item {
  /**
   * @param {Object} config from config/consumables.json, including
   *   `effect: { type, amount, maxShield?, useTime? }` and `maxStack`
   */
  constructor(config = {}) {
    const maxStack = config.maxStack || 1;
    super({
      ...config,
      type: ItemType.CONSUMABLE,
      isEquippable: true,
      isStackable: true,
      maxStackSize: maxStack
    });

    this.maxStack = maxStack;

    /** @type {{ type?: string, amount?: number, maxShield?: number, useTime?: number }} */
    const effect = config.effect || {};
    this.effect = {
      type: effect.type || ConsumableEffectType.HEALTH_RESTORE,
      amount: Number.isFinite(effect.amount) ? effect.amount : 0,
      // The most this consumable can bring the pool to — a small potion tops
      // you to 50 shield however much you already had.
      maxShield: Number.isFinite(effect.maxShield) ? effect.maxShield : null,
      useTime: Number.isFinite(effect.useTime) ? effect.useTime : DEFAULT_USE_TIME
    };
  }

  /** @returns {number} ms the player must stand still to finish a use */
  get useTime() {
    return this.effect.useTime;
  }

  /**
   * Apply this consumable's effect to a player.
   *
   * Kept here rather than in the system so that what an effect *does* lives
   * with the item, and adding a third effect type is a case in one switch.
   *
   * @param {{ shieldHealth: number, maxShield?: number, health: object, heal: Function }} player
   * @returns {{ type: string, applied: number }} how much actually landed
   */
  applyTo(player) {
    if (!player) return { type: this.effect.type, applied: 0 };

    switch (this.effect.type) {
      case ConsumableEffectType.SHIELD_RESTORE: {
        const before = player.shieldHealth || 0;
        // Two caps: what the item can top you to, and the player's own ceiling
        // when they have a real one. `maxShield` is 0 or absent for a player
        // with no configured ceiling (the same reading HealAbility takes), and
        // that must not mean "this potion does nothing".
        const playerCap = Number.isFinite(player.maxShield) && player.maxShield > 0
          ? player.maxShield
          : Infinity;
        const cap = Math.min(this.effect.maxShield ?? Infinity, playerCap);
        const after = Math.min(before + this.effect.amount, cap);
        // Never take shield away from someone already above the cap.
        player.shieldHealth = Math.max(before, after);
        return { type: this.effect.type, applied: player.shieldHealth - before };
      }

      case ConsumableEffectType.HEALTH_RESTORE: {
        const before = player.health?.currentHealth ?? 0;
        player.heal?.(this.effect.amount);
        return { type: this.effect.type, applied: (player.health?.currentHealth ?? 0) - before };
      }

      default:
        return { type: this.effect.type, applied: 0 };
    }
  }
}

export { Consumable, ConsumableEffectType };
